import * as React from 'react';
import { ReactElement, ReactNode, useContext, useEffect } from 'react';
import {
  AdminDataPersonDTO,
  ExternalIdDTO,
  PersonDTO,
  PersonEmailDTO,
  PersonInstitutionalIdDTO,
  PersonNameDTO,
  PersonStatusDTO,
  PersonToOrganisationDTO,
  ReferencedProjectDTO,
  SubjectDTO,
  UrlDTO,
} from '../../../api/core';
import { AuthContext } from '../../../Auth/components/AuthProvider';
import { Layout } from '../../Layout';
import { IntlShape, useIntl } from 'react-intl';
import { FormContent } from '../FormContent';
import { Validation } from '../../../util/validation/interfaces';
import { validate } from '../../../util/validation/validate';
import { CheckPermissionFunction, PermissionGate } from '../../../Auth/components/PermissionGate';
import { PersonNames } from '../components/parts/PersonNames';
import { PersonStatuses } from '../components/parts/PersonStatuses';
import { Block } from '../components/layout/Block';
import { Urls } from '../components/parts/Urls';
import { Emails } from '../components/parts/Emails';
import { ExternalIds } from '../components/parts/ExternalIds';
import { InstitutionalIds } from '../components/parts/InstitutionalIds';
import { EditorialStatus } from '../components/parts/EditorialStatus';
import { ReferencedProjects } from '../components/parts/ReferencedProjects';
import { PersonOrganisations } from '../components/parts/PersonOrganisations';
import { LabelledInputNumber } from '../../components/form/LabelledInputNumber';
import { LabelledInputTextarea } from '../../components/form/LabelledInputTextarea';
import { getVocabularyOptions } from '../../../Vocabulary/util';
import { Hidden } from '../components/parts/Hidden';
import { LabelledInputText } from '../../components/form/LabelledInputText';
import { LabelledCalendar } from '../../components/form/LabelledCalendar';
import { Subjects } from '../components/parts/Subjects';

export interface EditProps {
  modifiedModel: PersonDTO;
  submit: () => void;
  updateField: (field: keyof PersonDTO, value: any) => void;
  updateAdminField: (field: keyof AdminDataPersonDTO, value: any) => void;
  saving: boolean;
}

export function FormPerson(props: EditProps): ReactNode {
  const intl: IntlShape = useIntl();
  const {session} = useContext(AuthContext);

  const validation: Validation = validate(props.modifiedModel, props.modifiedModel.adminDataPerson, 'person', session);

  const [initiallyLoaded, setInitiallyLoaded] = React.useState(false);
  useEffect(() => {
    setInitiallyLoaded(true);
  }, []);

  return (
    <Layout
      title={
        props.modifiedModel.id
          ? intl.formatMessage({ id: 'person.header.edit' })
          : intl.formatMessage({ id: 'person.header.create' })
      }
      headerImage={require('../../../Settings/img/banners/edit/person.svg')}
    >
      <FormContent onSave={props.submit} validation={validation} isNew={!props.modifiedModel.id} saving={props.saving}>
        <Block title={'person.header.basic'}>
          <PermissionGate always={false} entity="person" field="person_names">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <PersonNames
                  autoFocus={initiallyLoaded}
                  title={'person.attr.personNames'}
                  personNames={props.modifiedModel.personNames}
                  onChange={(personNames: PersonNameDTO[]): void => props.updateField('personNames', personNames)}
                  checkPermission={checkPermission}
                  validation={validation?.root?.fields?.person_names}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="person" field="person_statuses">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <PersonStatuses
                  autoFocus={initiallyLoaded}
                  title={'person.attr.personStatuses'}
                  checkPermission={checkPermission}
                  statuses={props.modifiedModel.personStatuses}
                  validation={validation?.root?.fields?.person_statuses}
                  onChange={(statuses: PersonStatusDTO[]): void => props.updateField('personStatuses', statuses)}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="person" field="urls">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <Urls
                  autoFocus={initiallyLoaded}
                  title={'person.attr.urls'}
                  urls={props.modifiedModel.urls || []}
                  onChange={(urls: UrlDTO[]): void => props.updateField('urls', urls)}
                  typelist={'url_list'}
                  checkPermission={checkPermission}
                  validation={validation?.root?.fields?.urls}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="person" field="person_emails">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <Emails
                  autoFocus={initiallyLoaded}
                  title={'person.attr.personEmails'}
                  emails={props.modifiedModel.personEmails}
                  onChange={(emails: PersonEmailDTO[]): void => props.updateField('personEmails', emails)}
                  checkPermission={checkPermission}
                  validation={validation?.root?.fields?.person_emails}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="person" field="note">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <LabelledInputTextarea
                  id={'note'}
                  value={props.modifiedModel.note || ''}
                  validation={validation?.root?.fields?.note}
                  permissions={checkPermission()}
                  onChange={(value): void => props.updateField('note', value)}
                  autoResize={true}
                  rows={3}
                  title={'person.attr.note'}
                />
              </div>
            )}
          </PermissionGate>
        </Block>
        <Block title={'person.header.ids'}>
          <PermissionGate always={false} entity="person" field="external_ids">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <ExternalIds
                  autoFocus={initiallyLoaded}
                  extIds={props.modifiedModel.externalIds || []}
                  onChange={(externalIds: ExternalIdDTO[]): void => props.updateField('externalIds', externalIds)}
                  options={getVocabularyOptions(intl.locale, 'external_person_id_list')}
                  validation={validation?.root?.fields?.external_ids}
                  checkPermission={checkPermission}
                  title={'person.attr.externalIds'}
                  label={'person.attr.externalId'}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="person" field="person_institutional_ids">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <InstitutionalIds
                  autoFocus={initiallyLoaded}
                  institutionalIds={props.modifiedModel.personInstitutionalIds || []}
                  onChange={(personInstitutionalIds: PersonInstitutionalIdDTO[]): void =>
                    props.updateField('personInstitutionalIds', personInstitutionalIds)
                  }
                  validation={validation?.root?.fields?.person_institutional_ids}
                  checkPermission={checkPermission}
                  typesList={'affiliations_list'}
                  title={'person.attr.institutionalIds'}
                />
              </div>
            )}
          </PermissionGate>
        </Block>
        <Block title={'person.header.administration'}>
          <PermissionGate always={false} entity="person" field="id">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <LabelledInputNumber
                  id={'id'}
                  value={props.modifiedModel.id || null}
                  validation={validation?.root?.fields?.id}
                  permissions={checkPermission()}
                  min={0}
                  allowEmpty={true}
                  maxFractionDigits={0}
                  useGrouping={false}
                  onChange={(): void => console.warn('ID cannot be edited')}
                  title={'person.attr.id'}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="person_admin_data" field="hidden">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12 flex'}>
                <Hidden
                  label={'person.attr.hidden'}
                  checked={props.modifiedModel.adminDataPerson.hidden || false}
                  disabled={!checkPermission().canWrite}
                  invalid={!validation?.root?.fields?.id.valid}
                  required={checkPermission().required}
                  onChange={(hidden: boolean): void => props.updateAdminField('hidden', hidden)}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="person_admin_data" field="data_last_supplied">
            {(checkPermission: CheckPermissionFunction) => (
              <div className={'col-12'}>
                <LabelledCalendar
                  id={'data_last_supplied'}
                  value={props.modifiedModel.adminDataPerson.dataLastSupplied}
                  permissions={checkPermission()}
                  validation={validation?.admin?.fields?.data_last_supplied}
                  onChange={(value: Date): void => props.updateAdminField('dataLastSupplied', value)}
                  title={'person.attr.lastSupplied'}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="person_admin_data" field="person_checked">
            {(checkPermission: CheckPermissionFunction) => (
              <div className={'col-12'}>
                <LabelledCalendar
                  id={'person_checked'}
                  value={props.modifiedModel.adminDataPerson.personChecked}
                  permissions={checkPermission()}
                  validation={validation?.admin?.fields?.person_checked}
                  onChange={(value: Date): void => props.updateAdminField('personChecked', value)}
                  title={'person.attr.personChecked'}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="person_admin_data" field="editorial_status">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <EditorialStatus
                  editorialStatus={props.modifiedModel.adminDataPerson.editorialStatus}
                  onChange={(status: string): void => props.updateAdminField('editorialStatus', status)}
                  checkPermission={checkPermission}
                  validation={validation?.admin?.fields?.editorial_status}
                  label={'person.attr.editorialStatus'}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="person_admin_data" field="changed">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <LabelledCalendar
                  id={'changed'}
                  value={props.modifiedModel.adminDataPerson.changed || null}
                  permissions={checkPermission()}
                  title={'person.attr.changed'}
                  onChange={(): void => console.warn('Changed cannot be edited')}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="person_admin_data" field="created">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <LabelledCalendar
                  id={'created'}
                  value={props.modifiedModel.adminDataPerson.created || null}
                  permissions={checkPermission()}
                  title={'person.attr.created'}
                  onChange={(): void => console.warn('Created cannot be edited')}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="person_admin_data" field="origin">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <LabelledInputText
                  id={'origin'}
                  value={props.modifiedModel.adminDataPerson.origin || null}
                  permissions={checkPermission()}
                  validation={validation?.admin?.fields?.origin}
                  title={'person.attr.origin'}
                  onChange={(): void => console.warn('Origin cannot be edited')}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="person_admin_data" field="deskman">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <LabelledInputText
                  id={'deskman'}
                  value={props.modifiedModel.adminDataPerson.deskman || null}
                  permissions={checkPermission()}
                  validation={validation?.admin?.fields?.deskman}
                  title={'person.attr.deskman'}
                  onChange={(): void => console.warn('deskman cannot be edited')}
                />
              </div>
            )}
          </PermissionGate>
        </Block>
        <Block title={'person.header.linking'}>
          <PermissionGate always={false} entity="person" field="person_subjects">
            {(checkPermission: CheckPermissionFunction): ReactElement => (
              <div className={'col-12'}>
                <Subjects
                  autoFocus={initiallyLoaded}
                  title={'person.attr.subjects'}
                  checkPermission={checkPermission}
                  onChange={(subjects: SubjectDTO[]): void => props.updateField('personSubjects', subjects)}
                  subjects={props.modifiedModel.personSubjects || []}
                  validation={validation?.root?.fields?.person_subjects}
                  type={'person'}
                />
              </div>
            )}
          </PermissionGate>
          <PermissionGate always={false} entity="person" field="person_organisations">
            {(checkPermission: CheckPermissionFunction) => (
              <div className={'col-12'}>
                <PersonOrganisations
                  autoFocus={initiallyLoaded}
                  title={'person.attr.personOrganisations'}
                  personOrganisations={props.modifiedModel.personOrganisations || []}
                  onChange={(organisations: PersonToOrganisationDTO[]): void =>
                    props.updateField('personOrganisations', organisations)
                  }
                  validation={validation?.root?.fields?.person_organisations}
                  checkPermission={checkPermission}
                />
              </div>
            )}
          </PermissionGate>
        </Block>
      </FormContent>
    </Layout>
  );
}

export default FormPerson;
